// notd compatibility: read the .zip notd exports and write the same format back.
// notd layout (notd 5.6 public/js/main.js, buildLocalExportZip / parseFrontmatter):
//   "YYYY-MM-DD title.md" at the root, in "FolderName/" or in "archived/"; "_notd/" is skipped;
//   optional frontmatter `created`, `due` (YYYY-MM-DD, midnight UTC), `color` (red|amber|green|
//   blue|purple|gray), `pinned: true`; the rest of the file is the Markdown body.

import { unzipSync, zipSync, strFromU8, strToU8 } from 'fflate';
import type { NoteFields } from './doc';

export const COLORS = ['red', 'amber', 'green', 'blue', 'purple', 'gray'] as const;

export type ImportedNote = { path: string; folder: string | null; fields: Partial<NoteFields>; body: string };

const dayMs = (stamp: string) => Date.parse(`${stamp}T00:00:00Z`);
const stamp = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function parseFrontmatter(text: string): { body: string; created: number | null; due: number | null; color: string | null; pinned: boolean } {
  const out = { body: text, created: null as number | null, due: null as number | null, color: null as string | null, pinned: false };
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?\r?\n?/.exec(text);
  if (!m) return out;
  const date = (v: string) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? dayMs(v) : null);
  for (const line of m[1].split(/\r?\n/)) {
    const kv = /^([A-Za-z][\w-]*)\s*:\s*(.*)$/.exec(line);
    if (!kv) continue;
    const [key, value] = [kv[1].toLowerCase(), kv[2].trim()];
    if (key === 'created') out.created = date(value);
    else if (key === 'due') out.due = date(value);
    else if (key === 'color') out.color = (COLORS as readonly string[]).includes(value) ? value : null;
    else if (key === 'pinned') out.pinned = /^(true|yes|1)$/i.test(value);
  }
  out.body = text.slice(m[0].length);
  return out;
}

/** "2026-03-01 Courses-2.md" → { created: 2026-03-01, title: "Courses" } */
export function parseFileName(name: string): { title: string; created: number | null } {
  const base = name.replace(/\.md$/i, '');
  const m = /^(\d{4}-\d{2}-\d{2}) (.*)$/.exec(base);
  const title = (m ? m[2] : base).replace(/-\d+$/, '').trim();
  return { title: title === 'untitled' ? '' : title, created: m ? dayMs(m[1]) : null };
}

export function readNotdZip(bytes: Uint8Array): ImportedNote[] {
  const files = unzipSync(bytes);
  const out: ImportedNote[] = [];
  for (const [path, data] of Object.entries(files)) {
    if (!/\.md$/i.test(path) || path.startsWith('_notd/') || path.startsWith('__MACOSX/')) continue;
    const parts = path.split('/');
    const file = parts.pop()!;
    const dir = parts.join('/');
    const fm = parseFrontmatter(strFromU8(data));
    const fromName = parseFileName(file);
    out.push({
      path,
      folder: dir && dir !== 'archived' ? dir : null,
      fields: {
        title: fromName.title,
        created: fm.created ?? fromName.created ?? Date.now(),
        due: fm.due,
        color: fm.color,
        pinned: fm.pinned,
        archived: dir === 'archived',
      },
      body: fm.body,
    });
  }
  return out;
}

export type ExportNote = { fields: NoteFields; body: string; folderName: string | null; edited: number };

/** Same sanitising as notd's safeExportName. */
export function safeExportName(title: string): string {
  return (title || 'untitled').replace(/[/\\?%*:|"<>]/g, '-').replace(/\s+/g, ' ').trim().slice(0, 120) || 'untitled';
}

export function frontmatter(f: NoteFields): string {
  const lines = [`created: ${stamp(f.created || Date.now())}`];
  if (f.due != null) lines.push(`due: ${stamp(f.due)}`);
  if (f.color) lines.push(`color: ${f.color}`);
  if (f.pinned) lines.push('pinned: true');
  return `---\n${lines.join('\n')}\n---\n\n`;
}

export function writeNotdZip(notes: ExportNote[]): Uint8Array {
  const used = new Set<string>();
  const files: Record<string, Uint8Array> = {};
  for (const n of [...notes].sort((a, b) => b.edited - a.edited)) {
    const dir = n.fields.archived ? 'archived/' : n.folderName ? `${safeExportName(n.folderName)}/` : '';
    const base = `${stamp(n.fields.created || n.edited)} ${safeExportName(n.fields.title)}`;
    let name = `${dir}${base}.md`;
    for (let k = 2; used.has(name); k++) name = `${dir}${base}-${k}.md`;
    used.add(name);
    files[name] = strToU8(frontmatter(n.fields) + n.body);
  }
  return zipSync(files, { level: 0 });
}
